import { Grid, Stack, TextField } from "@mui/material";
import React from "react";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { DesktopDatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { objectVals2String } from "../../../utils/objectVals2String";

interface Values {
  date: Date;
}

interface Props {
  initDate?: Date;
  minDate?: Date;
  isMaxDateToday?: boolean;
  maxDate?: Date;
  dateLabel?: string;
  onComplete: (date: Date) => Promise<boolean>;
  onBack?: () => void;
  centerButtons?: boolean;
}

export const SingleDateForm = ({
  initDate,
  minDate,
  isMaxDateToday,
  maxDate,
  dateLabel,
  onComplete,
  onBack,
  centerButtons,
}: Props): JSX.Element => {
  // https://formik.org/docs/api/formik

  // deals with weird yup date max behavior
  //  DesktopDatePicker and yup.date().max() seem to interpret max date in conflicting ways
  const effectiveMaxDate = isMaxDateToday ? new Date() : maxDate;

  const formikInit = {
    values: {
      date: initDate || new Date(),
    },
    schema: Yup.object().shape({
      date: Yup.date()
        .required(`Oops, ${dateLabel} is required`)
        .min(minDate, `Oops, date must be after ${minDate?.toDateString()}`)
        .max(
          effectiveMaxDate,
          `Oops, date must be before ${effectiveMaxDate?.toDateString()}`
        ),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      await onComplete(values.date);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      validationSchema={formikInit.schema}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
    >
      {({
        errors,
        // handleBlur,
        // handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
        status,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack spacing={3}>
                  <DesktopDatePicker
                    label={dateLabel || "Date"}
                    inputFormat="MM/dd/yyyy"
                    minDate={minDate}
                    maxDate={maxDate}
                    value={values.date || ""}
                    onChange={(date) => setFieldValue("date", date)}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.date && errors.date)}
                        helperText={touched.date && errors.date}
                        {...params}
                      />
                    )}
                  />
                </Stack>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
                centered={centerButtons}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
